import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import {
  Avatar,
  Box,
  Checkbox,
  CircularProgress,
  Divider,
  List,
  ListItem,
  ListItemText,
  Popover,
  styled,
  Typography,
} from '@mui/material';
import { ChevronDown, ChevronRight, Workflow } from 'lucide-react';
import { useAppDispatch, useAppSelector } from 'src/hooks/useStore';
import {
  fetchOrganizations,
  setSelectedOrganization,
} from 'src/store/slices/organization.slice';
import { SonioDemoFlowFilterContext } from 'src/contexts/SonioDemoFlowFilterContext';
import { customerFlowNames } from 'src/components/CustomerId';
import {
  buildFlowNameLookup,
  isSonioDemoOrganization,
} from 'src/services/organizations.service';
import { getCmsApiOrigin } from 'src/services/api.config';
import { extractProfileFlowIds, getProfile } from 'src/services/users.service';
import { getFreshToken } from 'src/utils/get-fresh-token';

const MenuUserBox = styled(Box)(
  ({ theme }) => `
    background: #F9FAFB;
    padding: ${theme.spacing(2)};
    border-radius: 16px;
    border: 1px solid #E5E7EB;
`,
);

const UserBoxText = styled(Box)(
  ({ theme }) => `
    text-align: left;
    padding-left: ${theme.spacing(1)};
`,
);

const UserBoxLabel = styled(Typography)(
  ({}) => `
    font-weight: 500;
    color: #374151;
    display: block;
    font-size: 14px;

    &.popoverTypo {
      color: #374151;
    }
`,
);

const getOrganizationLogo = (org: {
  id: string;
  branding?: { logo_url?: string };
}) => {
  const logoUrl = org.branding?.logo_url;

  if (logoUrl) {
    if (logoUrl.startsWith('http://') || logoUrl.startsWith('https://')) {
      return logoUrl;
    }
    if (logoUrl.startsWith('/')) {
      return `${getCmsApiOrigin()}${logoUrl}`;
    }
    return logoUrl;
  }
  return undefined;
};

const capitalizeFirstLetter = (string: string) =>
  string.charAt(0).toUpperCase() + string.slice(1);

const getOrgDisplayName = (
  org: { name?: string; display_name?: string } | null,
) => org?.display_name ?? org?.name ?? '';

const AUTH0_NS = 'https://dev-sitk24ss2iowl228.eu.auth0.com';

function extractRoles(claims: Record<string, unknown> | undefined): string[] {
  const raw =
    claims?.['user/roles'] ??
    claims?.['roles'] ??
    claims?.[`${AUTH0_NS}/roles`];
  return Array.isArray(raw) ? (raw as string[]) : [];
}

function SidebarTopSection({ collapsed }: { collapsed: boolean }) {
  const { user, getIdTokenClaims, isLoading } = useAuth0();
  const dispatch = useAppDispatch();
  const { organizations, selectedOrganization } = useAppSelector(
    state => state.organization,
  );
  const { liveData } = useAppSelector(state => state.sliceTransaction);
  const flowFilter = useContext(SonioDemoFlowFilterContext);

  const [isOpen, setOpen] = useState<boolean>(false);
  const [showText, setShowText] = useState<boolean>(true);
  const [userRoles, setUserRoles] = useState<string[]>([]);
  const [flowIdsOpen, setFlowIdsOpen] = useState<boolean>(false);
  const [profileFlowIds, setProfileFlowIds] = useState<string[]>([]);
  const [flowsLoading, setFlowsLoading] = useState<boolean>(false);
  const ref = useRef<any>(null);

  const flowNameLookup = useMemo(
    () =>
      selectedOrganization?.flows
        ? buildFlowNameLookup([selectedOrganization])
        : (customerFlowNames as Record<string, string>),
    [selectedOrganization],
  );
  const flows = profileFlowIds.map(id => ({
    id,
    name: flowNameLookup[id] ?? id,
  }));

  const isSonioDemoOrg = isSonioDemoOrganization(selectedOrganization);
  // NOTE: We previously also required the user to carry a "sonio-demo" Auth0 role.
  // This caused the picker to disappear for admins or when the roles claim shape
  // changed in Auth0. Showing the picker for anyone inside the Sonio Demo org
  // is safe (it only filters what THIS user sees on dashboards/sessions).
  const showFlowIdsDropdown = flowFilter != null && isSonioDemoOrg;
  // Suppress unused-var lint (kept for potential future role-based UI).
  void userRoles;

  const getOrganizations = useCallback(async () => {
    try {
      if (!user?.sub) {
        return;
      }
      const claims = await getIdTokenClaims();
      const __raw = claims?.__raw;

      if (!__raw) {
        // eslint-disable-next-line no-console
        console.error('No token available from Auth0');
        return;
      }

      dispatch(fetchOrganizations({ sub: user.sub, __raw }));
    } catch (err) {
      // Handle error silently or with proper error handling
      // eslint-disable-next-line no-console
      console.error('Error fetching organizations:', err);
    }
  }, [dispatch, getIdTokenClaims, user?.sub]);

  useEffect(() => {
    if (user?.sub) {
      getOrganizations();
    }
  }, [getOrganizations, user?.sub, liveData]);

  useEffect(() => {
    if (!organizations || organizations.length === 0) return;

    // Persist org selection across refreshes.
    const storedOrgId = localStorage.getItem('selectedOrganizationId') ?? '';
    const next =
      organizations.find(o => String(o?.id ?? '') === storedOrgId) ??
      // If stored is not found, keep current selection if still present.
      (selectedOrganization
        ? organizations.find(
            o => String(o?.id ?? '') === String(selectedOrganization.id ?? ''),
          )
        : undefined) ??
      // Otherwise, fall back to the first entry (usually "All Organizations").
      organizations[0];

    if (next) {
      const idChanged =
        String(next.id ?? '') !==
        String(selectedOrganization?.id ?? '__none__');
      // Also update when flowIds changed (e.g. switching between live / staging
      // returns the same org ID but different flow IDs).
      const flowIdsChanged =
        JSON.stringify(next.flowIds ?? []) !==
        JSON.stringify(selectedOrganization?.flowIds ?? []);
      if (idChanged || flowIdsChanged) {
        dispatch(setSelectedOrganization(next));
      }
    }
  }, [organizations, dispatch, selectedOrganization]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const claims = await getIdTokenClaims();
        if (cancelled) return;
        setUserRoles(extractRoles(claims ?? undefined));
      } catch {
        if (!cancelled) setUserRoles([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [getIdTokenClaims]);

  const lastProfileFlowIdsKeyRef = useRef<string>('');

  // When Sonio Demo is selected, fetch profile and use profile.flowids as the only list for the sidebar
  // Capture the latest flowFilter in a ref so the fetch effect doesn't
  // depend on the context object identity (which changes on every state
  // update inside the provider → would cause an infinite refetch loop).
  const flowFilterRef = useRef(flowFilter);
  useEffect(() => {
    flowFilterRef.current = flowFilter;
  }, [flowFilter]);

  useEffect(() => {
    if (!showFlowIdsDropdown) {
      setProfileFlowIds([]);
      flowFilterRef.current?.setProfileFlowIds([]);
      return;
    }
    const orgFlowIds = selectedOrganization?.flowIds ?? [];
    let cancelled = false;
    setFlowsLoading(true);
    (async () => {
      try {
        const token = await getFreshToken(getIdTokenClaims);
        if (!token || cancelled) return;
        const profile = await getProfile(token, liveData);
        if (cancelled) return;
        const profileIds = extractProfileFlowIds(profile);
        const ids = profileIds.length > 0 ? profileIds : orgFlowIds;
        setProfileFlowIds(ids);
        const ff = flowFilterRef.current;
        ff?.setProfileFlowIds(ids);
        const key = ids.slice().sort().join(',');
        if (ff && ids.length > 0 && lastProfileFlowIdsKeyRef.current !== key) {
          lastProfileFlowIdsKeyRef.current = key;
          ff.setSelectedFlowIds([...ids]);
        }
      } catch {
        if (!cancelled) {
          const fallbackIds = orgFlowIds;
          setProfileFlowIds(fallbackIds);
          flowFilterRef.current?.setProfileFlowIds(fallbackIds);
        }
      } finally {
        if (!cancelled) setFlowsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // Intentionally omit `flowFilter` — handled via ref to avoid refetch loops.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    showFlowIdsDropdown,
    getIdTokenClaims,
    selectedOrganization?.id,
    selectedOrganization?.flowIds,
    liveData,
  ]);

  // Don't render until user is loaded
  if (isLoading || !user) {
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <CircularProgress size={20} />
      </Box>
    );
  }

  const handleOpen = (): void => {
    setOpen(true);
  };

  const handleClose = (): void => {
    setOpen(false);
  };

  // eslint-disable-next-line react-hooks/rules-of-hooks
  useEffect(() => {
    if (collapsed) {
      setShowText(false);
    } else {
      const timer = setTimeout(() => {
        setShowText(true);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [collapsed]);
  return (
    <>
      {organizations && organizations.length >= 1 && selectedOrganization && (
        <>
          <div
            ref={ref}
            onClick={organizations.length > 1 ? handleOpen : undefined}
            className={`${
              collapsed ? 'h-[48px] px-[12px]' : 'h-[60px] px-[16px]'
            } py-[10px] flex items-center justify-between gap-[10px] rounded-xl bg-white border-[1.5px] border-[#E5E7EB] ${
              organizations.length > 1
                ? 'hover:border-[#00387D] cursor-pointer'
                : ''
            } transition-colors duration-200`}
          >
            <div
              className={`flex items-center gap-3 ${
                collapsed ? 'justify-center w-full' : ''
              }`}
            >
              <Avatar
                variant="rounded"
                alt={getOrgDisplayName(selectedOrganization)}
                src={getOrganizationLogo(selectedOrganization)}
                onError={e => {
                  // eslint-disable-next-line no-console
                  console.error(
                    'Failed to load logo for organization:',
                    getOrgDisplayName(selectedOrganization),
                    'URL:',
                    getOrganizationLogo(selectedOrganization),
                  );
                  e.currentTarget.style.display = 'none';
                }}
                sx={{
                  width: collapsed ? 24 : 32,
                  height: collapsed ? 24 : 32,
                  borderRadius: '8px',
                  bgcolor: '#0C2340',
                  fontSize: collapsed ? 12 : 14,
                  fontWeight: 700,
                  '& img': {
                    objectFit: 'contain',
                    width: '100%',
                    height: '100%',
                  },
                }}
              >
                {getOrgDisplayName(selectedOrganization)
                  ?.charAt(0)
                  ?.toUpperCase() || 'O'}
              </Avatar>
              {!collapsed && showText && (
                <span className="font-semibold text-[#0C2340] text-sm">
                  {getOrgDisplayName(selectedOrganization)}
                </span>
              )}
            </div>
            {!collapsed && organizations.length > 1 && (
              <ChevronDown className="text-[#5B6B80]" size={16} />
            )}
          </div>

          {/* Flow dropdown: only when Sonio Demo org is selected and role is sonio-demo */}
          {showFlowIdsDropdown && !collapsed && (
            <div className="mt-2 rounded-xl bg-white border-[1.5px] border-[#E5E7EB] overflow-hidden">
              <button
                type="button"
                onClick={() => setFlowIdsOpen(prev => !prev)}
                className="w-full flex items-center gap-2 px-3 py-2.5 text-left text-sm font-medium text-[#0C2340] hover:bg-[#EAF0F9] transition-colors"
              >
                <ChevronRight
                  className="w-4 h-4 transition-transform flex-shrink-0"
                  style={{
                    transform: flowIdsOpen ? 'rotate(90deg)' : 'rotate(0deg)',
                  }}
                />
                <Workflow className="w-4 h-4 text-[#00387D]" />
                <span>Flow</span>
              </button>
              {flowIdsOpen && (
                <div className="border-t border-[#E5E7EB] max-h-[240px] overflow-y-auto px-2 pb-2">
                  {flowsLoading ? (
                    <div className="flex items-center justify-center py-4">
                      <CircularProgress size={20} />
                    </div>
                  ) : flows.length === 0 ? (
                    <p className="text-xs text-[#5B6B80] py-3 px-1">
                      No flows found
                    </p>
                  ) : (
                    flows.map(flow => (
                      <label
                        key={flow.id}
                        className="flex items-center gap-2 py-1.5 px-1 rounded-lg hover:bg-[#EAF0F9] cursor-pointer"
                      >
                        <Checkbox
                          size="small"
                          checked={flowFilter!.selectedFlowIds.includes(
                            flow.id,
                          )}
                          onChange={() => flowFilter!.toggleFlowId(flow.id)}
                          sx={{
                            color: '#00387D',
                            '&.Mui-checked': { color: '#00387D' },
                            padding: 0.5,
                          }}
                        />
                        <span
                          className="text-xs text-[#374151] truncate"
                          title={flow.id}
                        >
                          {flow.name || flow.id}
                        </span>
                      </label>
                    ))
                  )}
                </div>
              )}
            </div>
          )}
        </>
      )}

      {organizations && organizations.length > 1 && selectedOrganization && (
        <>
          <Popover
            disableScrollLock
            anchorEl={ref.current}
            onClose={handleClose}
            open={isOpen}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'left',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'left',
            }}
            PaperProps={{
              sx: {
                borderRadius: '16px',
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
                border: '1px solid #E5E7EB',
                overflow: 'hidden',
              },
            }}
          >
            <MenuUserBox
              sx={{
                minWidth: 210,
              }}
              display="flex"
            >
              <Avatar
                variant="rounded"
                alt={getOrgDisplayName(selectedOrganization)}
                src={getOrganizationLogo(selectedOrganization)}
                onError={e => {
                  // eslint-disable-next-line no-console
                  console.error(
                    'Failed to load logo in popover:',
                    getOrgDisplayName(selectedOrganization),
                  );
                  e.currentTarget.style.display = 'none';
                }}
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: '8px',
                  '& img': {
                    objectFit: 'contain',
                    width: '100%',
                    height: '100%',
                  },
                }}
              >
                {getOrgDisplayName(selectedOrganization)
                  ?.charAt(0)
                  ?.toUpperCase() || 'O'}
              </Avatar>
              <UserBoxText className="flex items-center">
                <UserBoxLabel className="popoverTypo" variant="body1">
                  {capitalizeFirstLetter(
                    getOrgDisplayName(selectedOrganization),
                  )}
                </UserBoxLabel>
              </UserBoxText>
            </MenuUserBox>
            <Divider
              sx={{
                mb: 0,
                borderColor: '#E5E7EB',
              }}
            />
            <List
              sx={{
                p: 1,
                maxHeight: '400px',
                overflowY: 'auto',
                '&::-webkit-scrollbar': {
                  width: '8px',
                },
                '&::-webkit-scrollbar-track': {
                  background: '#F3F4F6',
                  borderRadius: '4px',
                },
                '&::-webkit-scrollbar-thumb': {
                  background: '#D1D5DB',
                  borderRadius: '4px',
                  '&:hover': {
                    background: '#9CA3AF',
                  },
                },
              }}
              component="nav"
            >
              <div>
                {organizations &&
                  organizations.map((org, index) => (
                    <ListItem
                      key={index}
                      onClick={() => {
                        dispatch(setSelectedOrganization(org));
                        localStorage.setItem(
                          'selectedOrganizationId',
                          String(org?.id ?? ''),
                        );
                        handleClose();
                      }}
                      button
                      sx={{
                        borderRadius: '12px',
                        margin: '2px 4px',
                        '&:hover': {
                          backgroundColor: '#F3F4F6',
                        },
                      }}
                    >
                      <Avatar
                        variant="rounded"
                        alt={getOrgDisplayName(org)}
                        src={getOrganizationLogo(org)}
                        onError={e => {
                          // eslint-disable-next-line no-console
                          console.error(
                            'Failed to load logo in list:',
                            getOrgDisplayName(org),
                          );
                          e.currentTarget.style.display = 'none';
                        }}
                        sx={{
                          width: 32,
                          height: 32,
                          borderRadius: '8px',
                          '& img': {
                            objectFit: 'contain',
                            width: '100%',
                            height: '100%',
                          },
                        }}
                      >
                        {getOrgDisplayName(org)?.charAt(0)?.toUpperCase() ||
                          'O'}
                      </Avatar>
                      <ListItemText
                        className="pl-4"
                        primary={capitalizeFirstLetter(getOrgDisplayName(org))}
                        sx={{
                          '& .MuiListItemText-primary': {
                            fontSize: '14px',
                            fontWeight: 500,
                            color: '#374151',
                          },
                        }}
                      />
                    </ListItem>
                  ))}
              </div>
            </List>
          </Popover>
        </>
      )}
    </>
  );
}

export default SidebarTopSection;
